import React from 'react';
import {List} from 'immutable';
import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';
import {componentHoc} from 'utils/mixin/componentHoc';
import ConstValues from 'utils/const';
import CallerTeamComboboxSearchFieldActions from 'actions/search/CallerTeamComboboxSearchFieldActions';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import IconButtonGroup from './IconButtonGroup';
import MultipleSelectField from 'components/pure/utils/select-field/MultipleSelectField';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

const COMBO_BOX_OPTIONS = List([
  {
    className: 'combobox-search-field-team-search-button',
    key: 'team_search',
  },
  {className: 'combobox-search-field-rep-search-button', key: 'rep_search'},
  {
    className: 'combobox-search-field-manager-search-button',
    key: 'manager_search',
  },
]);

const COMBO_BOX_LABELS = List([
  {name: 'Team', key: 'team_search'},
  {name: 'Rep', key: 'rep_search'},
  {name: 'Manager', key: 'manager_search'},
]);

/**
 * `CallerTeamComboboxSearchField` renders a 3-choice-box + a multiple-select dropdown search field for 'Team', 'Rep',
 * 'Manager'. Each choice-box corresponds to a different set of teams / reps / managers search
 */
@componentHoc
export default class CallerTeamComboboxSearchField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSearchTerm: '',
      selectedOptionKey: 'rep_search',
    };
  }

  componentDidMount() {
    if (this.props.searchFieldsState) {
      this._updateComboboxSelectionState(this.props);
    }
  }

  /**
   * Invoked when the component's DOM representation has been updated.
   * Scrolls to the widget if it is the most recent Search Widgets to be toggled on.
   */
  componentDidUpdate() {
    if (this.props.shouldFocus) {
      this.props.scrollWidgetIntoViewIfNecessary();
      // Reset the last toggled or it will keep focusing the last component on all future renders.
      this.props.updateLastToggledOnSearchWidget(null);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.searchFieldsState !== nextProps.searchFieldsState) {
      this._updateComboboxSelectionState(nextProps);
    }
  }

  _updateComboboxSelectionState(props) {
    const searchFieldState = props.searchFieldsState;
    if (searchFieldState) {
      const target = searchFieldState.get('target');
      if (target) {
        const savedSelectedOptionKey = `${target}_search`;
        // Need to fetch the info about teams / reps / managers from the server-side in order to populate the local list of
        // selected callers in the search sidebar
        if (savedSelectedOptionKey === 'team_search') {
          const teamIds = searchFieldState.get('values');
          CallerTeamComboboxSearchFieldActions.fetchSelectedTeams.defer(teamIds);
        } else if (savedSelectedOptionKey === 'rep_search') {
          const repIds = searchFieldState.get('values');
          CallerTeamComboboxSearchFieldActions.fetchSelectedReps.defer(repIds);
        } else if (savedSelectedOptionKey === 'manager_search') {
          const managerIds = searchFieldState.get('values');
          CallerTeamComboboxSearchFieldActions.fetchSelectedManagers.defer(managerIds);
        }
        this.setState({selectedOptionKey: savedSelectedOptionKey});
      }
    }
  }
  /**
   * Callback function for the toggle of the caller team combobox search widget.
   * @param {object} event An event object
   * @param {boolean} toggled A boolean indicating whether the search widget is toggled or not
   */
  onToggleSearchField = (event, toggled) => {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.comboboxWidgetType,
      toggled,
      this.props.searchField
    );

    if (!toggled) {
      CallerTeamComboboxSearchFieldActions.resetCaller.defer();
      this.setState({selectedOptionKey: 'rep_search', currentSearchTerm: ''});
    } else {
      this.props.updateLastToggledOnSearchWidget(this.props.searchField.name);
    }
  };

  onTouchTapIconButtonGroupButton = key => {
    this.props.onUpdateSelectedOptionKey(this.props.searchField, key);
    this.setState({selectedOptionKey: key, currentSearchTerm: ''});
  };

  removeCallerComboboxItem = item => {
    const selectedOptionKey = this.state.selectedOptionKey;
    CallerTeamComboboxSearchFieldActions.removeCaller({
      caller: item,
      selectedOptionKey: selectedOptionKey,
    });
  };

  handleSearchFieldOpenChange = open => {
    if (open) {
      let search = '';
      if (!!this.state.currentSearchTerm && this.state.currentSearchTerm.length > 0) {
        search = this.state.currentSearchTerm;
      }
      this._search(search);
    }
  };

  handleSearchFieldChange = callerId => {
    const caller = this._caller(callerId, this.state.selectedOptionKey);
    CallerTeamComboboxSearchFieldActions.changeCaller({
      caller: caller,
      selectedOptionKey: this.state.selectedOptionKey,
    });
  };

  _caller(callerId, selectedOptionKey) {
    if (selectedOptionKey === 'team_search') {
      return this.props.teams.find(team => team.id === callerId);
    } else if (selectedOptionKey === 'rep_search') {
      return this.props.reps.find(rep => rep.id === callerId);
    } else if (selectedOptionKey === 'manager_search') {
      return this.props.managers.find(rep => rep.id === callerId);
    }
  }

  handleSearchFieldSearchTermChange = searchTerm => {
    this.setState({currentSearchTerm: searchTerm});
    this._search(searchTerm);
  };

  _search(searchTerm) {
    if (this.state.selectedOptionKey === 'team_search') {
      CallerTeamComboboxSearchFieldActions.searchTeams(searchTerm);
    } else if (this.state.selectedOptionKey === 'rep_search') {
      CallerTeamComboboxSearchFieldActions.searchReps(searchTerm);
    } else if (this.state.selectedOptionKey === 'manager_search') {
      CallerTeamComboboxSearchFieldActions.searchManagers(searchTerm);
    }
  }

  searchFieldOptions() {
    if (this.state.selectedOptionKey === 'team_search') {
      return this._searchFieldOptions(this.props.teams, this.props.selectedTeams);
    } else if (this.state.selectedOptionKey === 'rep_search') {
      return this._searchFieldOptions(this.props.reps, this.props.selectedReps);
    } else if (this.state.selectedOptionKey === 'manager_search') {
      return this._searchFieldOptions(this.props.managers, this.props.selectedManagers);
    }
  }

  _searchFieldOptions(options, selected) {
    return options
      .filterNot(opt => {
        return selected.findLast(item => item.id === opt.id);
      })
      .map(opt => {
        if (opt && (opt.first_name || opt.last_name)) {
          return {value: opt.id, label: `${opt.first_name} ${opt.last_name}`};
        }
        return {value: opt.id, label: opt.name};
      })
      .toArray();
  }

  currentCallers() {
    const selectedOptionKey = this.state.selectedOptionKey;
    if (selectedOptionKey === 'team_search') {
      return this.props.selectedTeams.toArray();
    } else if (selectedOptionKey === 'rep_search') {
      return this.props.selectedReps.toArray();
    } else if (selectedOptionKey === 'manager_search') {
      return this.props.selectedManagers.toArray();
    }
  }

  noResultsMessage() {
    const selectedOptionKey = this.state.selectedOptionKey;
    if (selectedOptionKey === 'team_search') {
      return 'No teams matching your search';
    } else if (selectedOptionKey === 'rep_search') {
      return 'No users matching your search';
    } else if (selectedOptionKey === 'manager_search') {
      return 'No managers matching your search';
    }
  }

  renderRepManagerTeamFilter() {
    return (
      <div>
        <IconButtonGroup
          buttonOptions={COMBO_BOX_OPTIONS}
          buttonLabels={COMBO_BOX_LABELS}
          selectedOptionKey={this.state.selectedOptionKey}
          highlightMode="foreground"
          onTouchTapIconButton={this.onTouchTapIconButtonGroupButton}
          iconButtonLabelStyle={{marginTop: '0px'}}
        />
        <MultipleSelectField
          removeItem={this.removeCallerComboboxItem}
          selectField={{
            name: 'caller',
            className: 'search-sidebar-select-field',
            placeholder: 'Type to search',
            noResultsMessage: this.noResultsMessage(),
            handleOpenChange: this.handleSearchFieldOpenChange,
            options: this.searchFieldOptions(),
            onChange: this.handleSearchFieldChange,
            onSearchChange: this.handleSearchFieldSearchTermChange,
            search: this.state.currentSearchTerm,
            filterOptions: (options, values, search) => options,
          }}
          selectedList={{
            containerClassname: 'combobox-list-container',
            listClassname: 'combobox-list',
            spanClassname: 'combobox-span',
            items: this.currentCallers(),
          }}
        />
      </div>
    );
  }

  render() {
    const showSearchWidget = this.props.toggled && this.props.canToggleCallerField;
    const classes = cn('combobox-search-widget', getSearchFieldClass(this.props.searchField.name));
    return (
      <div className={classes}>
        <Toggle
          label={this.props.searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleSearchField}
          toggled={showSearchWidget}
        />
        <Divider className="combobox-divider" />
        {showSearchWidget && <div>{this.renderRepManagerTeamFilter()}</div>}
      </div>
    );
  }
}
